export class BannerComponent {
  private static modalListenersBound = false;

  private static findModalWrapper(targetEl: Element | null): HTMLElement | null {
    return (targetEl?.closest('.cmp-container__modal') as HTMLElement | null) ?? null;
  }

  private static setModalOpen(wrapper: HTMLElement, isOpen: boolean): void {
    if (isOpen) {
      wrapper.classList.remove('hide');
      wrapper.classList.add('is-open');
      wrapper.setAttribute('aria-hidden', 'false');
      wrapper.removeAttribute('inert');
      document.documentElement.classList.add('modal-open');
    } else {
      wrapper.classList.add('hide');
      wrapper.classList.remove('is-open');
      wrapper.setAttribute('aria-hidden', 'true');
      wrapper.setAttribute('inert', '');
      document.documentElement.classList.remove('modal-open');
    }
  }

  private static openModalById(id: string): void {
    const target = document.getElementById(id);
    const wrapper = this.findModalWrapper(target);
    if (!wrapper) return;

    this.setModalOpen(wrapper, true);
  }

  private static closeModalFromInside(elInsideModal: Element): void {
    const wrapper = this.findModalWrapper(elInsideModal);
    if (!wrapper) return;

    this.setModalOpen(wrapper, false);
  }

  private static closeOpenModal(): void {
    const openWrapper = document.querySelector<HTMLElement>('.cmp-container__modal.is-open');
    if (!openWrapper) return;

    this.setModalOpen(openWrapper, false);
  }

  static initBannerModalHandlers(): void {
    if (this.modalListenersBound) return;
    this.modalListenersBound = true;

    document.addEventListener('click', (e) => {
      const link = (e.target as Element).closest('a.cmp-banner__action-link');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      if (!href.startsWith('#')) return;

      const id = href.slice(1);
      if (!id) return;

      e.preventDefault();
      this.openModalById(id);
    });

    document.addEventListener('click', (e) => {
      const closeBtn = (e.target as Element).closest('[data-modal-close]');
      if (!closeBtn) return;

      e.preventDefault();
      this.closeModalFromInside(closeBtn);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      this.closeOpenModal();
    });
  }

  // Apply background styles to banner elements
  static applyBannerBackgroundStyles(): void {
    try {
      const bannerElements = document.querySelectorAll('.cmp-banner');

      if (bannerElements.length === 0) {
        return;
      }

      bannerElements.forEach((element) => {
        const bannerElement = element as HTMLElement;
        this.checkTelInTitle(bannerElement);

        // Handle background color
        const backgroundColor = bannerElement.getAttribute('data-background-color');
        if (backgroundColor && backgroundColor.trim() !== '') {
          bannerElement.style.backgroundColor = backgroundColor;
        }

        // Handle background image
        const useImageAsBackground = bannerElement.getAttribute('data-background-image');
        if (useImageAsBackground === 'true') {
          const imageElement = bannerElement.querySelector(
            '.cmp-image img, .cmp-teaser__image img'
          );
          if (imageElement) {
            const imageUrl = (imageElement as HTMLImageElement).getAttribute('src');
            if (imageUrl) {
              bannerElement.style.backgroundImage = `url(${imageUrl})`;
              bannerElement.style.backgroundSize = 'cover';
              bannerElement.style.backgroundPosition = 'center center';
              bannerElement.style.backgroundRepeat = 'no-repeat';
            }
          }
        }
      });
    } catch (error) {
      console.error('Error applying banner background styles:', error);
    }
  }

  // check if we have a tel in title and if we have replace spaces
  // after prefix with \u00A0 so safari cant detect is a phone number
  // and remove it. Also put in regex \s+ so we discard number like year 2028
  static checkTelInTitle(banner: Element): void {
    function addNbspAfterPrefix(text: string) {
      const phoneRegex = /(\+?\d{1,4}|\(?\d{2,4}\)?)\s+(\d[\d\s-]{5,})/g;

      return text.replace(phoneRegex, (match, prefix, rest) => {
        const cleanPrefix = prefix.replace(/\s+/g, '');
        return cleanPrefix + '\u00A0' + rest.trim() + '\u00A0';
      });
    }

    const bannerTitle = banner.querySelector('.cmp-banner__content > .cmp-banner__title');
    if (!bannerTitle) return;
    const newTitle = addNbspAfterPrefix(bannerTitle.textContent ?? '');
    bannerTitle.textContent = newTitle;
  }

  // Apply button placement based on container classes
  static applyBannerButtonPlacement(): void {
    try {
      const bannerElements = document.querySelectorAll('.cmp-banner');

      if (bannerElements.length === 0) {
        return;
      }

      bannerElements.forEach((element) => {
        const banner = element as HTMLElement;
        const actionContainer = banner.querySelector('.cmp-banner__action-container');
        const contentDiv = banner.querySelector('.cmp-banner__content');

        if (!actionContainer || !contentDiv) {
          return;
        }

        let currentElement: Element | null = banner;
        let hasFoundPrimaryStyle = false;
        let hasFoundSecondaryStyle = false;

        while (currentElement && !(hasFoundPrimaryStyle || hasFoundSecondaryStyle)) {
          if (
            currentElement.classList.contains('primary-button') ||
            currentElement.classList.contains('-golden')
          ) {
            hasFoundPrimaryStyle = true;
            break;
          }

          if (
            currentElement.classList.contains('secondary-button') ||
            currentElement.classList.contains('secondary-button-dark')
          ) {
            hasFoundSecondaryStyle = true;
            break;
          }

          currentElement = currentElement.parentElement;
        }

        if (hasFoundPrimaryStyle) {
          if (actionContainer.parentElement !== contentDiv) {
            contentDiv.appendChild(actionContainer);
          }
        } else if (actionContainer.parentElement === contentDiv) {
          banner.appendChild(actionContainer);
        }
      });
    } catch (error) {
      console.error('Error applying banner button placement:', error);
    }
  }
  static init(): void {
    this.initBannerModalHandlers();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.applyBannerBackgroundStyles();
        this.applyBannerButtonPlacement();
      });
    } else {
      this.applyBannerBackgroundStyles();
      this.applyBannerButtonPlacement();
    }

    document.addEventListener('cq-editables-updated', () => {
      this.applyBannerBackgroundStyles();
      this.applyBannerButtonPlacement();
    });
  }
}
BannerComponent.init();
